import {
	Box,
	Paper,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
} from "@mui/material";
import { type ReactNode, useState } from "react";
import {
	CONSUMPTION_CHART_METRIC,
	useConsumptionChartMetric,
} from "../../shared/consumption/hooks/useConsumptionChartMetric";
import {
	UTILITY_CONSUMPTION_CHART_METRIC,
	type UtilityConsumptionChartMetric,
	type UtilityConsumptionMainBucket,
	type UtilityConsumptionMeter,
	type UtilityConsumptionMode,
	type UtilityConsumptionPeriod,
	type UtilityConsumptionSummary,
} from "../../shared/consumption/models/UtilityConsumption";
import { RATIO_UNIT, VOLUME_UNIT } from "../../shared/consumption/utils/format";
import { CONSUMPTION_LABEL_SX } from "../../shared/consumption/utils/tokens";
import { buildConsumptionChartModel } from "../../shared/consumption/components/consumption-chart/chartModel";
import { ConsumptionChartHeader } from "../../shared/consumption/components/consumption-chart/ConsumptionChartHeader";
import { ConsumptionDetailGrid } from "../../shared/consumption/components/consumption-chart/ConsumptionDetailGrid";
import { PeriodBarsChart } from "../../shared/consumption/components/consumption-chart/PeriodBarsChart";

interface ConsumptionChartProps {
	buckets: UtilityConsumptionMainBucket[];
	meters: UtilityConsumptionMeter[];
	summary: UtilityConsumptionSummary;
	mode: UtilityConsumptionMode;
	period: UtilityConsumptionPeriod;
	selectedMeterIds: string[] | null;
	controls: ReactNode;
	onMeterFilter: (meterId: string) => void;
}

export const ConsumptionChart = ({
	buckets,
	meters,
	summary,
	mode,
	period,
	selectedMeterIds,
	controls,
	onMeterFilter,
}: ConsumptionChartProps) => {
	const [highlightedBucket, setHighlightedBucket] = useState<number | null>(
		null,
	);
	const [selectedMetric, setSelectedMetric] = useConsumptionChartMetric();

	const hasGallonsPerTon = buckets.some(
		(bucket) => bucket.GallonsPerTon !== null,
	);
	const metric =
		selectedMetric === CONSUMPTION_CHART_METRIC.RATIO && hasGallonsPerTon
			? UTILITY_CONSUMPTION_CHART_METRIC.GALLONS_PER_TON
			: UTILITY_CONSUMPTION_CHART_METRIC.GALLONS;
	const isRatio = metric === UTILITY_CONSUMPTION_CHART_METRIC.GALLONS_PER_TON;

	const model = buildConsumptionChartModel({
		buckets,
		meters,
		mode,
		metric,
	});

	const meterScopeLabel =
		selectedMeterIds?.map(model.getSeriesName).join(", ") ?? "All";

	return (
		<Paper
			component="section"
			variant="outlined"
			sx={{
				containerType: "inline-size",
				display: "flex",
				flex: 1,
				flexDirection: "column",
				minHeight: 0,
				minWidth: 0,
				p: 1.25,
			}}
		>
			<ConsumptionChartHeader
				period={period}
				isPartial={summary.IsMainPartial}
				total={isRatio ? summary.MainGallonsPerTon : summary.MainTotalGallons}
				unit={model.unit}
				meterScopeLabel={meterScopeLabel}
			/>
			<Box
				sx={{
					alignItems: "center",
					display: "flex",
					flexWrap: "wrap",
					gap: 1,
					justifyContent: "space-between",
					minWidth: 0,
					mt: 1,
					width: "100%",
				}}
			>
				{controls}
				<ToggleButtonGroup
					exclusive
					size="small"
					value={metric}
					aria-label="Consumption chart metric"
					sx={{ ml: "auto" }}
					onChange={(_, value: UtilityConsumptionChartMetric | null) =>
						value &&
						setSelectedMetric(
							value === UTILITY_CONSUMPTION_CHART_METRIC.GALLONS_PER_TON
								? CONSUMPTION_CHART_METRIC.RATIO
								: CONSUMPTION_CHART_METRIC.TOTAL,
						)
					}
				>
					<ToggleButton
						value={UTILITY_CONSUMPTION_CHART_METRIC.GALLONS}
						aria-label="Gallons view"
					>
						{VOLUME_UNIT}
					</ToggleButton>
					<Tooltip
						title={
							hasGallonsPerTon
								? "Gallons per ton rolled in each period"
								: "No production tons reported for this period"
						}
					>
						<span>
							<ToggleButton
								value={UTILITY_CONSUMPTION_CHART_METRIC.GALLONS_PER_TON}
								aria-label="Gallons per ton view"
								disabled={!hasGallonsPerTon}
							>
								{RATIO_UNIT}
							</ToggleButton>
						</span>
					</Tooltip>
				</ToggleButtonGroup>
			</Box>
			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: "minmax(0, 1fr)",
					gridTemplateRows: "auto",
					gap: 1,
					flex: "0 0 auto",
					mt: 1,
					minHeight: 0,
					minWidth: 0,
					width: "100%",
					["@container (min-width: 1140px)"]: {
						flex: 1,
						gridTemplateColumns: "minmax(0, 1fr) minmax(200px, 250px)",
						gridTemplateRows: "minmax(0, 1fr)",
					},
				}}
			>
				<Box
					sx={{
						alignSelf: "stretch",
						height: { xs: 260, sm: 300 },
						minWidth: 0,
						width: "100%",
						["@container (min-width: 1140px)"]: { height: "auto" },
					}}
				>
					<PeriodBarsChart
						model={model}
						highlightedBucket={highlightedBucket}
						onMeterFilter={onMeterFilter}
					/>
				</Box>
				<Box
					sx={{
						alignSelf: "stretch",
						display: "flex",
						flexDirection: "column",
						height: 340,
						minWidth: 0,
						width: "100%",
						["@container (min-width: 1140px)"]: { height: "auto" },
					}}
				>
					<Typography component="div" sx={{ ...CONSUMPTION_LABEL_SX, pb: 0.5 }}>
						Detail
					</Typography>
					<ConsumptionDetailGrid
						rows={model.detailRows}
						unit={model.unit}
						total={
							isRatio ? summary.MainGallonsPerTon : summary.MainTotalGallons
						}
						onRowHover={(rowId) =>
							setHighlightedBucket(
								rowId === null
									? null
									: (model.bucketIndexByRowId.get(rowId) ?? null),
							)
						}
					/>
				</Box>
			</Box>
		</Paper>
	);
};
